import type { ReactElement } from "react";
import {
  SuspensePageBoundary,
  StudyLayoutSkeleton,
} from "@-/dev.improve.haniverse.components.ui.suspense-page";
import {
  LearnFlashcardStage,
  type LearnFlashcardStageProps,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.deck._components.card";
import {
  LessonFlashcardBack,
  LessonFlashcardControls,
  LessonFlashcardFront,
  LessonFlashcardInfo,
  LessonFlashcardProvidersSuspense,
  MorphemeLessonLayoutSuspense,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.new-morpheme";

/**
 * A NEW-CHARACTER LESSON as a deck card: the "New Character" front, the mnemonics back (with its
 * meaning and pronunciation tabs) and the Info surface, under the lesson layout context that the
 * existing lesson components read. A pronunciation-level lesson (`part: "pronunciation"`) is the
 * same card introducing only the character's readings.
 */
export function LearnLessonFlashcard(
  props: Omit<LearnFlashcardStageProps, "back" | "front" | "info"> & {
    slot: LearnFlashcardStageProps["slot"] & { item: { kind: "lesson" } };
    suspenseFallback?: ReactElement;
  },
) {
  const { grapheme, part, pinyin } = props.slot.item;
  return (
    <SuspensePageBoundary
      fallback={props.suspenseFallback ?? <StudyLayoutSkeleton />}
      location={import.meta.location}
    >
      <MorphemeLessonLayoutSuspense grapheme={grapheme} part={part} pinyin={pinyin}>
        <LessonFlashcardProvidersSuspense>
          {props.role === "current" ? <LessonFlashcardControls /> : null}
          <LearnFlashcardStage
            {...props}
            back={<LessonFlashcardBack />}
            front={<LessonFlashcardFront />}
            info={<LessonFlashcardInfo />}
          />
        </LessonFlashcardProvidersSuspense>
      </MorphemeLessonLayoutSuspense>
    </SuspensePageBoundary>
  );
}
